// TODO: Revisit caching logic once centralized caching logic is setup
class MediaGallery extends HTMLElement {
	static SELECTORS = {
		ACTIVE_DOT: 'media-gallery__dot--active',
		ACTIVE_THUMBNAIL: 'media-gallery__thumbnail-button--active',
		DOT: 'media-gallery__dot',
		DOTS_CONTAINER: 'media-gallery__dots-container',
		GALLERY: 'media-gallery__image-gallery',
		GALLERY_IMAGE: 'media-gallery__image',
		HIDDEN: 'hidden',
		MEDIA_MODAL: 'media-modal',
		NEXT_BUTTON: '[data-media-gallery-next]',
		PREV_BUTTON: '[data-media-gallery-prev]',
		THUMBNAIL: 'media-gallery__thumbnail-button',
		THUMBNAILS_WRAPPER: 'media-gallery__thumbnails-wrapper',
		VIEW: 'media-gallery__view',
		VIEW_WRAPPER: 'media-gallery__view-wrapper',
	};
	static SINGLE_COMPONENT_GROUPS = [
		{ name: 'dotsContainer', selector: `.${MediaGallery.SELECTORS.DOTS_CONTAINER}` },
		{ name: 'gallery', selector: `.${MediaGallery.SELECTORS.GALLERY}` },
		{ name: 'mediaModal', selector: MediaGallery.SELECTORS.MEDIA_MODAL },
		{ name: 'nextButton', selector: MediaGallery.SELECTORS.NEXT_BUTTON },
		{ name: 'prevButton', selector: MediaGallery.SELECTORS.PREV_BUTTON },
		{ name: 'thumbnailsWrapper', selector: `.${MediaGallery.SELECTORS.THUMBNAILS_WRAPPER}` },
		{ name: 'view', selector: `.${MediaGallery.SELECTORS.VIEW}` },
	];

	static COMPONENT_GROUPS = [
		{ name: 'dots', selector: MediaGallery.SELECTORS.DOT },
		{ name: 'galleryImages', selector: MediaGallery.SELECTORS.GALLERY_IMAGE },
		{ name: 'thumbnails', selector: MediaGallery.SELECTORS.THUMBNAIL },
	];

	constructor() {
		super();
		const { selectedVariant, showAll } = this.dataset;
		this.cache = {};
		this.elements = {};
		this.showAll = showAll === 'true';
		this.resetVariant();
		this.addGlobalEventListeners();
		this.cacheVariantImages();
		if (!selectedVariant) {
			return;
		}
		this.scrollGalleryToVariant(selectedVariant, false);
	}
	addEventListeners() {
		const { dots, thumbnails, galleryImages, nextButton, prevButton, view } = this.elements;

		if (nextButton) {
			nextButton.addEventListener('click', this.next.bind(this));
		}
		if (prevButton) {
			prevButton.addEventListener('click', this.prev.bind(this));
		}
		view.addEventListener('pointerdown', this.handleMouseDown.bind(this));
		view.addEventListener('scroll', debounce(this.handleGalleryScroll.bind(this), 100));
		if (dots) {
			Object.values(dots).forEach((dot) => dot.addEventListener('click', this.handleComponentClick.bind(this)));
		}
		if (thumbnails) {
			Object.values(thumbnails).forEach((thumb) =>
				thumb.addEventListener('click', this.handleComponentClick.bind(this))
			);
		}
		Object.values(galleryImages).forEach((image) =>
			image.addEventListener('click', this.handleGalleryImageClick.bind(this))
		);
	}
	addGlobalEventListeners() {
		window.addEventListener('resize', this.handleBrowserResize.bind(this));
		document.addEventListener('DOMContentLoaded', this.handleBrowserResize.bind(this));
		document.addEventListener('variant_change', this.handleVariantChange.bind(this));
	}
	cacheUrl(variantId) {
		const sectionId = this.dataset.sectionId ?? '';
		const url = `${this.dataset.url}?section_id=${sectionId}&variant=${variantId}`;

		fetch(url)
			.then((response) => response.text())
			.then((html) => {
				this.cache[url] = html;
			});
	}
	cacheVariantImages() {
		let variantIds = new Set([]);

		Object.values(this.elements.galleryImages).forEach((image) => {
			const { variants } = image.dataset;
			if (variants) {
				const variantsSplit = variants?.split(' ');
				variantsSplit.forEach((variant) => {
					variantIds.add(variant);
				});
			}
		});

		variantIds.forEach((id) => {
			this.cacheUrl(id);
		});
	}
	recalculatePosition() {
		const view = this.elements.view;
		const image_width = view.scrollWidth / this.visibleImages.length;
		const position = Math.round(view.scrollLeft / image_width);
		if (position === this.position) {
			return;
		}
		this.position = position;
	}
	registerComponents() {
		const componentGroups = MediaGallery.COMPONENT_GROUPS;
		componentGroups.forEach((component) => {
			const elementList = this.querySelectorAll(`.${component.selector}`);
			if (!elementList) {
				console.warn(component.name + ' is empty. Media Gallery may not work properly.');
				return;
			}
			this.populateElements(elementList, component.name);
		});
	}
	registerSingleComponents() {
		const componentGroups = MediaGallery.SINGLE_COMPONENT_GROUPS;
		componentGroups.forEach((component) => {
			const componentName = component.name;
			const element = this.querySelector(component.selector);
			if (!element) {
				console.warn(component.name + ' is missing to be rendered. Media Gallery may not work properly.');
				return;
			}
			this.elements[componentName] = this.querySelector(component.selector);
		});
	}
	handleGalleryScroll() {
		this.recalculatePosition();
		this.updateViewScroll(true);
	}
	populateElements(elementList, componentName) {
		this.elements[componentName] = {};

		elementList.forEach((element) => {
			const image_id = element.dataset.imageId;
			this.elements[componentName][image_id] = element;
		});
	}
	handleBrowserResize() {
		if (this.ticking) {
			return;
		}
		window.requestAnimationFrame(() => {
			this.updateTopOffset();
			this.updateViewScroll(false);
			this.ticking = false;
		});
		this.ticking = true;
	}
	handleGalleryImageClick(event) {
		const image_id = event.target.dataset.imageId;
		const mediaModal = this.elements.mediaModal;
		mediaModal.scrollImageIntoView(image_id);
	}

	handleMouseDown(event) {
		if (event.pointerType != 'mouse' || event.button != 0) {
			return;
		}
		const view = this.elements.view;

		this.mouseDown = true;
		this.mouseStart = event.clientX;
		this.viewScrollLeft = view.scrollLeft;
		document.addEventListener('pointermove', this.handleMouseMove.bind(this));
		document.addEventListener('pointerup', this.handleMouseUp.bind(this));
	}
	handleMouseMove(event) {
		if (!this.mouseDown) {
			return;
		}
		const view = this.elements.view;

		const delta = this.mouseStart - event.clientX;
		if (delta != 0) {
			this.addViewStyleOverrides();
		}
		view.scrollLeft = this.viewScrollLeft + delta;
	}
	handleMouseUp() {
		this.mouseDown = false;
		document.removeEventListener('pointermove', this.handleMouseMove);
		document.removeEventListener('pointerup', this.handleMouseUp);
		this.handleGalleryScroll();
		setTimeout(this.removeViewStyleOverrides.bind(this), 500);
	}
	handleComponentClick(event) {
		const image_id = event.target.dataset.imageId;
		this.scrollView(image_id);
	}
	handleVariantChange(event) {
		const { mediaModal } = this.elements;
		const selectors = [`.${MediaGallery.SELECTORS.GALLERY}`, `.${MediaGallery.SELECTORS.MEDIA_MODAL}`];
		const variant_id = event.detail.id;
		this.rerenderSections(variant_id, selectors);
		this.scrollGalleryToVariant(variant_id, false);
		mediaModal.setImages();
		if (!this.showAll) {
			this.filterImages(variant_id);
		}
	}
	hideButtons() {
		const { nextButton, prevButton } = this.elements;
		const activeClass = MediaGallery.SELECTORS.HIDDEN;
		if (!nextButton || !prevButton) {
			return;
		}
		nextButton.classList.add(activeClass);
		prevButton.classList.add(activeClass);
	}
	hideDotsContainer() {
		const { dotsContainer } = this.elements;
		if (!dotsContainer) {
			return;
		}
		dotsContainer.classList.add(MediaGallery.SELECTORS.HIDDEN);
	}
	hideThumbnailsContainer() {
		const { thumbnails_wrapper } = this.elements;
		if (!thumbnails_wrapper) {
			return;
		}
		thumbnails_wrapper.classList.add(MediaGallery.SELECTORS.HIDDEN);
	}
	rerenderFromCache(url, selectors) {
		const html = this.cache[url];
		this.rerenderComponents(html, selectors);
	}
	rerenderComponents(html, selectors) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
		for (const selector of selectors) {
			const element = document.querySelector(selector);
			const target = parsedHTML.querySelector(selector);
			if (element && target) {
				element.innerHTML = target.innerHTML;
			}
		}
		this.resetVariant();
		this.updateComponentStates();
	}
	rerenderFromFetch(url, selectors) {
		fetch(url)
			.then((response) => response.text())
			.then((html) => {
				this.cache[url] = html;
				this.rerenderComponents(html, selectors);
			});
	}
	rerenderSections(variantId, selectors) {
		const sectionId = this.dataset.sectionId ?? '';
		const url = `${this.dataset.url}?section_id=${sectionId}&variant=${variantId}`;
		if (this.cache.hasOwnProperty(url)) {
			this.rerenderFromCache(url, selectors);
		} else {
			this.rerenderFromFetch(url, selectors);
		}
	}
	resetVariant() {
		this.registerSingleComponents();
		this.registerComponents();
		this.resetVisibilityModel();
		this.addEventListeners();
	}
	scrollGalleryToVariant(variant_id, smoothScroll = true) {
		const galleryImages = Object.values(this.elements.galleryImages);
		const variantImg = galleryImages.find((img) => img.dataset.variants?.includes(variant_id));
		if (variantImg) {
			this.scrollView(variantImg.dataset.imageId, smoothScroll);
		} else {
			this.scrollView(galleryImages[0].dataset.imageId, smoothScroll);
		}
	}
	showButtons() {
		const { nextButton, prevButton } = this.elements;
		const activeClass = MediaGallery.SELECTORS.HIDDEN;
		if (!nextButton || !prevButton) {
			return;
		}
		nextButton.classList.remove(activeClass);
		prevButton.classList.remove(activeClass);
	}
	showDotsContainer() {
		const { dotsContainer } = this.elements;
		if (!dotsContainer) {
			return;
		}
		dotsContainer.classList.remove(MediaGallery.SELECTORS.HIDDEN);
	}
	showThumbnailsContainer() {
		const { thumbnailsWrapper } = this.elements;
		if (!thumbnailsWrapper) {
			return;
		}
		thumbnailsWrapper.classList.remove(MediaGallery.SELECTORS.HIDDEN);
	}
	getImageComponents(image_id) {
		const components = [];
		const galleryImage = this.elements.galleryImages[image_id];
		const dot = this.elements.dots ? this.elements.dots[image_id] : null;
		const thumbnail = this.elements.thumbnails ? this.elements.thumbnails[image_id] : null;
		components.push(galleryImage);
		if (dot) {
			components.push(dot);
		}
		if (thumbnail) {
			components.push(thumbnail);
		}
		return components;
	}
	showImage(image_id) {
		const components = this.getImageComponents(image_id);
		const mediaModal = this.elements.mediaModal;
		components.forEach((component) => component.classList.remove(MediaGallery.SELECTORS.HIDDEN));
		mediaModal.showImage(image_id);
	}
	hideImage(image_id) {
		const components = this.getImageComponents(image_id);
		const mediaModal = this.elements.mediaModal;
		components.forEach((component) => component.classList.add(MediaGallery.SELECTORS.HIDDEN));
		mediaModal.hideImage(image_id);
	}
	filterImages(variant_id) {
		Object.values(this.elements.galleryImages).forEach((image) => {
			let hidden = true;
			const { imageId, variants } = image.dataset;
			const containsVariantId = variants?.split(' ').includes(variant_id + '');
			if (!variants || containsVariantId) {
				hidden = false;
			}
			hidden ? this.hideImage(imageId) : this.showImage(imageId);
		});
		this.updateVisibleImages();
	}
	next() {
		this.position < this.visibleImages.length - 1 ? this.position++ : (this.position = 0);
		this.updateViewScroll(true);
	}
	prev() {
		this.position > 0 ? this.position-- : (this.position = this.visibleImages.length - 1);
		this.updateViewScroll(true);
	}
	addViewStyleOverrides() {
		const view = this.elements.view;
		view.classList.add('media-gallery__view-overrides');
	}
	removeViewStyleOverrides() {
		const view = this.elements.view;
		view.classList.remove('media-gallery__view-overrides');
	}
	resetVisibilityModel() {
		this.position = 0;
		this.updateVisibleImages();
		this.handleComponentVisibility();
	}
	updateVisibleImages() {
		this.visibleImages = [];
		Object.values(this.elements.galleryImages).forEach((image) => {
			if (!image.classList.contains(MediaGallery.SELECTORS.HIDDEN)) {
				const image_id = image.dataset.imageId;
				this.visibleImages.push(image_id);
			}
		});
	}
	handleComponentVisibility() {
		if (this.visibleImages.length > 1) {
			this.showButtons();
			this.showDotsContainer();
			this.showThumbnailsContainer();
		} else {
			this.hideButtons();
			this.hideDotsContainer();
			this.hideThumbnailsContainer();
		}
	}
	scrollView(image_id, smoothScroll = true) {
		this.position = this.visibleImages.indexOf(image_id);
		this.updateViewScroll(smoothScroll);
	}
	setActiveDot(image_id) {
		const activeClass = MediaGallery.SELECTORS.ACTIVE_DOT;
		const activeDot = this.elements.activeDot;
		if (!this.elements.dots || !activeDot) {
			return;
		}
		activeDot?.classList.remove(activeClass);
		this.elements.activeDot = this.elements.dots[image_id];
		this.elements.activeDot.classList.add(activeClass);
	}
	setActiveThumbnail(image_id) {
		if (!this.elements.thumbnails) {
			return;
		}
		const activeClass = MediaGallery.SELECTORS.ACTIVE_THUMBNAIL;
		const activeThumbnail = this.elements.activeThumbnail;
		activeThumbnail?.classList.remove(activeClass);
		this.elements.activeThumbnail = this.elements.thumbnails[image_id];
		this.elements.activeThumbnail?.classList.add(activeClass);
	}
	updateViewScroll(smoothScroll = true) {
		const { view } = this.elements;

		let params = { left: view.offsetWidth * this.position };
		if (smoothScroll) {
			params.behavior = 'smooth';
		}
		view.scroll(params);
		this.updateComponentStates();
	}
	updateComponentStates() {
		const { nextButton, prevButton } = this.elements;
		const image_id = this.visibleImages[this.position];
		if (nextButton) {
			nextButton.disabled = this.position >= this.visibleImages.length - 1;
		}
		if (prevButton) {
			prevButton.disabled = this.position <= 0;
		}
		if (image_id) {
			this.setActiveDot(image_id);
			this.setActiveThumbnail(image_id);
		}
	}
	updateTopOffset() {
		const header = document.querySelector('header.shopify-section.header');
		const gallery = this.elements.gallery;
		window.innerWidth >= 800 ? (gallery.style.top = `${header.offsetHeight}px`) : gallery.style.removeProperty('top');
	}
}
class MediaModal extends HTMLElement {
	static CLASSES = {
		ACTIVE: 'media-modal--active',
		HIDDEN: 'hidden',
		IMAGE: 'media-modal__image',
		OVERFLOW: 'overflow--hidden',
	};
	constructor() {
		super();
		this.setImages();
		this.addEventListener('click', this.close.bind(this));
	}
	close() {
		this.classList.remove(MediaModal.CLASSES.ACTIVE);
		document.body.classList.remove(MediaModal.CLASSES.OVERFLOW);
		document.body.style.removeProperty('padding-right');
	}
	hideImage(image_id) {
		const image = this.images[image_id];
		image.classList.add(MediaModal.CLASSES.HIDDEN);
	}
	open() {
		const scrollbarWidth = window.innerWidth - document.body.clientWidth;
		this.classList.add(MediaModal.CLASSES.ACTIVE);
		document.body.style.paddingRight = `${scrollbarWidth}px`;
		document.body.classList.add(MediaModal.CLASSES.OVERFLOW);
	}
	setImages() {
		this.images = {};
		this.querySelectorAll(`.${MediaModal.CLASSES.IMAGE}`).forEach((image) => {
			const image_id = image.dataset.imageId;
			this.images[image_id] = image;
		});
	}
	scrollImageIntoView(image_id) {
		const image = this.images[image_id];
		this.open();
		image.scrollIntoView({
			block: 'center',
			inline: 'center',
		});
	}
	showImage(image_id) {
		const image = this.images[image_id];
		image?.classList.remove(MediaModal.CLASSES.HIDDEN);
	}
}
if (!customElements.get('media-gallery')) {
	customElements.define('media-gallery', MediaGallery);
}
if (!customElements.get('media-modal')) {
	customElements.define('media-modal', MediaModal);
}
